import { type PricingModel, type StripeCoupon, type SubscriptionBillingCycle } from '@air/api/types';
import { MONTHS_IN_YEAR } from '@air/utilities';

export const CENTS_PER_DOLLAR = 100;

/**
 * Returns the number of units over the current storage. For example, if a user is 100 bytes over their current storage limit, this'll round up to the nearest unit of measurement (terabyte, gigabte, that you pass in).
 * @example // returns 2
 * getStorageOverage(2000, 1000)
 * @param {number} [overageStorage=0] - The total number of bytes the user is currently over
 * @param {number} [additionalStorage=1000] - The unit of measurement to count as increments for the overage
 * @returns {number} A number representing the number additional storage needed
 */
export const getStorageOverage = (overageStorage = 0, additionalStorage = 1000) =>
  overageStorage >= 0 ? Math.ceil(overageStorage / additionalStorage) : 0;

/**
 * Returns the number of bytes over max storage
 * @example // returns 100
 * getStorageOverageBytes(300, 100, 2)
 * @example // returns 0
 * getStorageOverageBytes(100, 200)
 * @param {number} usedStorage - The current number of bytes used as storage
 * @param {number} includedStorage - The included bytes on the plan
 * @returns {number} A number representing the number of bytes over max storage
 */
export const getStorageOverageBytes = (usedStorage = 0, includedStorage = 0) =>
  Math.max(0, usedStorage - includedStorage);

/**
 * This function returns the current price of a plan based on the number of users
 * @example // returns 400
 * getMemberPrice('perMember', 200, 2)
 * @param {PricingModel} [pricingModel] - The pricing model of the plan
 * @param {number} [planPrice] - The price of the plan in cents
 * @param {number} [currentMembers=1] - The current number of pending and active users in the workspace
 * @returns {number} The price of the members in cents
 */
export const getMemberPrice = (pricingModel: PricingModel, planPrice: number, currentMembers = 1) =>
  pricingModel === 'perMember' ? planPrice * currentMembers : planPrice;

/**
 * This function returns the total price of the additional storage overage
 * @example // returns 400
 * getTotalAdditionalStoragePrice(200, 2)
 * @param {number} [additionalStoragePrice] - The price for each unit of additional storage in cents
 * @param {number} [overage=0] - The amount of units over
 * @returns {number} The total price of the addtional storage in cents
 */
export const getTotalAdditionalStoragePrice = (additionalStoragePrice: number, overage = 0) =>
  overage >= 0 ? additionalStoragePrice * overage : 0;

/**
 * This function returns the total price of the plan
 * @example // returns 202
 * getTotalPlanPrice(200, 2)
 * @param {number} [memberPrice = 0] - The total price of the members in cents
 * @param {number} [totalAdditionalStoragePrice=0] - The total price of addtional storage in cents
 * @returns {number} The total price of the plan in cents
 */
export const getTotalPlanPrice = (totalAdditionalStoragePrice = 0, memberPrice = 0) =>
  memberPrice + totalAdditionalStoragePrice;

/**
 * Calculates the discount from a stripe coupon (handles both percentOff and amountOff)
 * @param totalPrice Total price before any coupon applied
 * @param coupon Stripe coupon info
 */
export const getDiscount = (totalPrice: number, coupon: StripeCoupon | null) => {
  if (!coupon) {
    return 0;
  }
  if (coupon.percentOff) {
    // biome-ignore lint/style/noMagicNumbers: percentage calculation
    return (totalPrice * coupon.percentOff) / 100;
  }

  if (coupon.amountOff) {
    return Math.min(coupon.amountOff, totalPrice);
  }

  return 0;
};

/**
 * Returns the floored percent usage of storage by pricing model
 * @example // returns 12
 * getStoragePercentageByPricingModel(25, 100)
 * @param {number} usedStorage
 * @param {number} maxStorageStaged
 */
export const getStoragePercentageByPricingModel = (usedStorage: number, maxStorageStaged: number) => {
  // biome-ignore lint/style/noMagicNumbers: percentage calculation
  return Math.floor((usedStorage / maxStorageStaged) * 100);
};

/**
 * Returns the annual price of a plan with `planPrice` at a given `billingCycle`
 * @example // returns 1000
 * calculateMonthlyPrice('monthly', 1000)
 * @example // returns 1200
 * calculateMonthlyPrice('annual', 12000)
 * @param {SubscriptionBillingCycle} billingCycle
 * @param {number} planPrice
 * @returns {number} monthly plan price in cents
 */
export const calculateMonthlyPrice = (billingCycle: SubscriptionBillingCycle, planPrice: number) => {
  const cyclesPerYear: { [key in SubscriptionBillingCycle]: number } = {
    annual: 1,
    monthly: 12,
    semiAnnual: 2,
    quarter: 4,
  };
  const annualPrice = planPrice * cyclesPerYear[billingCycle];

  return annualPrice / MONTHS_IN_YEAR;
};

export type NormalizedCreditCardBrand = 'Visa' | 'MasterCard' | 'American Express' | 'Discover';

export const normalizeCreditCardBrand = (brand: string): NormalizedCreditCardBrand | null => {
  switch (brand.toLowerCase()) {
    case 'visa':
      return 'Visa';
    case 'mastercard':
      return 'MasterCard';
    case 'american express':
    case 'amex':
      return 'American Express';
    case 'discover':
      return 'Discover';
    default:
      return null;
  }
};
